// ** Third Party
import { MultiValue, OptionsContainer } from "../components";
import { useEffect, useState } from "react";

import AsyncSelect from "react-select/async";
import AsyncSelectCreatable from "react-select/async-creatable";
import CustomTooltip from "@components/ui-components/tooltip";
// ** Reactstrap
import { Label } from "reactstrap";
import { REACT_STYLES } from "@src/utility/constants";
import classnames from "classnames";
import debounce from "lodash/debounce";
import { selectThemeColors } from "@helpers/utils";
import size from "lodash/size";
import { useTranslation } from "react-i18next";

const RawAsyncSelectField = (props) => {

    const { t } = useTranslation();

    const {
        className = "mb-1",
        placeholder = "",
        label = "",
        name,
        isMulti = false,
        disabled = false,
        id,
        tooltipContent = "",
        required = false,
        loading = false,
        customOnChange = () => {},
        isDirtyField = false,
        showErrMsg = true,
        loadOptions = () => {},
        selectClass = "",
        defaultOptions = false,
        isCreatable = false,
        isClearable = false,
        maxAllowedSelection = 10,
        onChange = () => { },
        value = "",
        message = "",
        isInvalid = false,
        selectRef,
        isSelectAll
    } = props;
    const options = defaultOptions;
    // To trigger focus
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [multi, setMulti] = useState(isMulti);
    const maxSelectedAllowed = maxAllowedSelection;
    const [showMaxSelectedWarning, setShowMaxSelectedWarning] = useState(false);
  
    const handleInputChange = (value) => {
        setMenuIsOpen(!!value); // Set menuIsOpen to true if value is truthy
    };

    /**
     * If there is an error message, return the error message
     * @returns message
     */
    const renderError = () => {
        return (
            message && (
                <small className="text-danger" data-cy={`error-${id}`}>
                    {message}
                </small>
            )
        );
    };
 
    const Select = isCreatable ? AsyncSelectCreatable : AsyncSelect;

    /**
   * A function that takes in two parameters, options and onChange. It then checks if the isMulti
   * variable is true. If it is, it will map over the options and return the value of each option. If
   * it is not true, it will return the value of the option.
   */
    const handleOnChange = (selectedOptions = {}) => {
    
        setMulti(isMulti);
        if (isMulti && maxSelectedAllowed && size(selectedOptions) > maxSelectedAllowed) {
            setShowMaxSelectedWarning(true);
            selectRef?.current.blur();
            setMenuIsOpen(false);
        } else {
            setShowMaxSelectedWarning(false);
            if (isMulti && !Array.isArray(selectedOptions)) onChange([selectedOptions]);
            else onChange(selectedOptions);
        }

    };

    useEffect(() => {
        if (size(value) && Array.isArray(value) && value?.filter((item) => item === "ALL") && isSelectAll) {
            setMulti(false);
        }
    }, []);
    return (
        <div
            id={`error-${name?.replaceAll(".", "-")}`}
            className={classnames({
                "mb-1"         : !className,
                [className]    : className,
                "single-select": !multi
            })}>
            {label && (
                <Label htmlFor={name} data-cy={`label-${id}`}>
                    {t(label)}
                    {required && (
                        <span className="text-danger fw-bolder">*</span>
                    )}
                    {Boolean(tooltipContent) && (
                        <>
                            <i
                                className="mdi mdi-information-outline ms-75"
                                id={`tooltip-select-${id}`}/>
                            <CustomTooltip
                                target={`tooltip-select-${id}`}
                                content={tooltipContent}/>
                        </>
                    )}
                </Label>
            )}
            <Select
                loadOptions={debounce((value, callback) => { loadOptions(value, callback); }, 1000)}
                ref={selectRef}
                value={value}
                id={`async-select-${id}`} 
                defaultValue={value}
                isMulti={multi}
                onInputChange={handleInputChange}
                cacheOptions
                components={{
                    Option: OptionsContainer,
                    MultiValue
                }}
                {...(!options ? { menuIsOpen } : {})}
                defaultOptions={options}
                classNamePrefix="select"
                styles={REACT_STYLES({ type: isInvalid ? "error" : showMaxSelectedWarning ? "warning" : "" })}
                theme={selectThemeColors}
                closeMenuOnSelect={!multi}
                escapeClearsValue={false}
                isClearable={isClearable}
                isDisabled={disabled}
                className={classnames("react-select", selectClass, {
                    "is-dirty-field":
                    isDirtyField && !isInvalid
                })}
                onBlur={() => setMenuIsOpen(true)}
                isLoading={loading}
                hideSelectedOptions={false}
                placeholder={label ? `${t("Search")} ${t(label)}` : t(placeholder)}
                onChange={selectedOptions => {
                    handleOnChange(selectedOptions);
                    if (customOnChange) {
                        customOnChange(selectedOptions);
                    }
                }}
                noOptionsMessage={() => "Sorry, no matching options."}/>
            {showMaxSelectedWarning  ? <p><small className="text-warning">{t("Max {{maxSelectedAllowed}} can be selected", { maxSelectedAllowed })}</small></p> : null}

            {(isInvalid && !showErrMsg && message) && (
                <CustomTooltip
                    target={`error-${name.replaceAll(".", "-")}`}
                    content={message}/>
            )}
            {showErrMsg && renderError()}
        </div>
    );
};

export default RawAsyncSelectField;
